import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {InteractiveSpendingTool} from '@/components/interactive-spending-tool'
import {Dialog, DialogContent, DialogHeader} from '@/components/ui/dialog'
import {localStorageAction} from '@/utils/common'

export const BuilderInteractiveSpendingTool = ({
    enableModal,
    modalTitle,
    modalDescription,
    modalDisclaimer,
    disclaimerText,
    defaultSpendingAmount,
    remainingText,
    ctaText,
    amountInputText,
    attributes,
    dataTestid
}) => {
    const {className, ...restAttributes} = attributes
    const [modalOpen, setModalOpen] = React.useState(true)
    const [hasSeenModal, setHasSeenModal] = React.useState(true)
    const localStorageKey = 'has-seen-interactive-spending-tool-modal'

    const handleModalClose = () => {
        setModalOpen(false)
        localStorageAction('set', localStorageKey, 'true')
    }

    const checkForLocalStorage = () => {
        const localStorageGet = localStorageAction('get', localStorageKey)
        if (localStorageGet === 'true') {
            setHasSeenModal(true)
            setModalOpen(false)
        } else {
            setHasSeenModal(false)
            setModalOpen(true)
        }
    }

    useEffect(() => {
        checkForLocalStorage()
    }, [])
    return (
        <>
            <InteractiveSpendingTool
                disclaimerText={disclaimerText}
                remainingText={remainingText}
                ctaText={ctaText}
                amountInputText={amountInputText}
                className={className}
                defaultSpendingAmount={defaultSpendingAmount}
                {...attributes}
                {...restAttributes}
                dataTestid={dataTestid || 'interactiveSpendingTool-InteractiveSpendingTool'}
            />
            {!hasSeenModal && enableModal ? (
                <Dialog
                    open={modalOpen}
                    onOpenChange={handleModalClose}
                    data-testid="expandContent-Dialog"
                >
                    <DialogContent
                        className="pt-9 px-10 max-w-lg"
                        data-testid="expandContent-DialogContent"
                    >
                        <DialogHeader data-testid="expandContent-DialogHeader">
                            <div className="flex flex-col items-center justify-center relative">
                                <h1 className="text-xl font-semibold">{modalTitle}</h1>
                                <p className="text-md mb-2 text-center">{modalDescription}</p>
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                    {modalDisclaimer}
                                </p>
                            </div>
                        </DialogHeader>
                    </DialogContent>
                </Dialog>
            ) : null}
        </>
    )
}

BuilderInteractiveSpendingTool.propTypes = {
    enableModal: PropTypes.bool,
    modalTitle: PropTypes.string,
    modalDescription: PropTypes.string,
    modalDisclaimer: PropTypes.string,
    disclaimerText: PropTypes.node,
    defaultSpendingAmount: PropTypes.number,
    remainingText: PropTypes.string,
    ctaText: PropTypes.string,
    amountInputText: PropTypes.string,
    attributes: PropTypes.any,
    dataTestid: PropTypes.string
}

export const InteractiveSpendingToolDefinition = {
    component: BuilderInteractiveSpendingTool,
    name: 'InteractiveSpendingTool',
    noWrap: true,
    inputs: [
        {
            name: 'enableModal',
            type: 'boolean',
            helperText: 'Enable or disable the modal for the Interactive Spending Tool',
            defaultValue: true
        },
        {
            name: 'modalTitle',
            type: 'string',
            helperText: 'Title for the modal dialog',
            defaultValue: 'Interactive Spending Tool'
        },
        {
            name: 'modalDescription',
            type: 'longtext',
            helperText: 'Description text for the modal dialog',
            defaultValue:
                'Enter your FSA balance to explore eligible health items tailored to your budget'
        },
        {
            name: 'modalDisclaimer',
            type: 'string',
            helperText: 'Disclaimer text for the modal dialog',
            defaultValue: 'Note: Any items already in your cart will adjust the remaining balance'
        },
        {
            name: 'disclaimerText',
            type: 'string',
            helperText: 'This text will be displayed under the progress bar',
            defaultValue: '*shipping fees, coupons, and taxes are not included'
        },
        {
            name: 'defaultSpendingAmount',
            type: 'number',
            helperText: 'The default spending amount to be used by the Spending Tool',
            defaultValue: 500
        },
        {
            name: 'remainingText',
            type: 'string',
            helperText: 'Text to display above the remaining amount',
            defaultValue: 'Remaining'
        },
        {
            name: 'ctaText',
            type: 'string',
            helperText: 'Text to display on the CTA button',
            defaultValue: 'GO'
        },
        {
            name: 'amountInputText',
            type: 'string',
            helperText: 'Text to display to the left of input field',
            defaultValue: 'Enter remaining FSA funds'
        },
        {
            name: 'dataTestid',
            type: 'string',
            helperText: 'Optional data-testid attribute for the main component wrapper',
            defaultValue: 'interactiveSpendingTool-InteractiveSpendingTool'
        }
    ]
}
